'use client';

import { forwardRef } from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import type { LucideIcon } from 'lucide-react';
import type { IconComponent, IconProps } from './types';

/**
 * Default stroke width for every Lucide glyph in the app. Lucide ships with 2;
 * this is the single knob to tune brand fit against the Capio system.
 */
export const ICON_STROKE_WIDTH = 2;

/**
 * Wraps a Lucide glyph in MUI's `SvgIcon` so call sites keep the same API as
 * `@mui/icons-material` (`fontSize`, `color`, `sx`, `titleAccess`, palette
 * overrides) and tests keep the `data-testid="{Name}Icon"` convention.
 *
 * `inheritViewBox` keeps Lucide's own 24x24 viewBox; `fill: none` undoes the
 * `fill: currentColor` that `SvgIcon` applies to non-`<svg>` children, which
 * would otherwise fill Lucide's stroke-only paths.
 */
export interface CreateLucideIconOptions {
  /**
   * Lucide ships outline glyphs only. `filled` paints the silhouette with
   * `currentColor` so a filled/outlined pair can still signal state (e.g. a
   * selected thumbs-up). Only use on glyphs whose inner strokes are optional.
   */
  filled?: boolean;
}

export function createLucideIcon(
  displayName: string,
  Glyph: LucideIcon,
  { filled = false }: CreateLucideIconOptions = {},
): IconComponent {
  const testId = `${displayName}Icon`;
  const fill = filled ? 'currentColor' : 'none';

  const Icon = forwardRef<SVGSVGElement, IconProps>(function LucideMuiIcon(
    { sx, strokeWidth = ICON_STROKE_WIDTH, ...rest },
    ref,
  ) {
    return (
      <SvgIcon
        ref={ref}
        component={Glyph}
        inheritViewBox
        data-testid={testId}
        sx={[{ fill }, ...(Array.isArray(sx) ? sx : [sx])]}
        // Lucide-specific attribute forwarded through SvgIcon to the glyph.
        {...{ strokeWidth }}
        {...rest}
      />
    );
  });

  Icon.displayName = testId;
  return Icon;
}
